import { Color3, Mesh, type Scene, StandardMaterial, VertexData } from "@babylonjs/core";
import type { RoadRect } from "../net/api";

type Height = (x: number, z: number) => number;

export const ROAD_LIFT = 0.02; // above the terrain, so the strips never z-fight with it
export const AVENUE_LIFT = 0.03; // a centimetre above the streets where the two meet (spec 2f §3.3)
export const ROAD_STEP = 2; // the terrain mesh spacing; finer cells would not follow it any better
const STYLE = { avenue: { color: "#8f8678", lift: AVENUE_LIFT }, road: { color: "#a39a8c", lift: ROAD_LIFT } } as const;

/** Ground-hugging quads: every rectangle is cut into cells of at most 2 m that follow the terrain (spec 2e §3.3, 2f §3.3). */
export function roadVertexData(rects: readonly RoadRect[], height: Height, lift: number): { positions: Float32Array; indices: Uint32Array } {
  const cells = rects.map((r) => [
    Math.max(1, Math.ceil((r.max[0] - r.min[0]) / ROAD_STEP - 1e-9)),
    Math.max(1, Math.ceil((r.max[1] - r.min[1]) / ROAD_STEP - 1e-9)),
  ] as const);
  const nv = cells.reduce((n, [nx, nz]) => n + (nx + 1) * (nz + 1), 0);
  const ni = cells.reduce((n, [nx, nz]) => n + nx * nz * 6, 0);
  const positions = new Float32Array(nv * 3), indices = new Uint32Array(ni);
  let p = 0, k = 0, base = 0;
  rects.forEach((r, n) => {
    const [nx, nz] = cells[n]!;
    for (let iz = 0; iz <= nz; iz++) {
      for (let ix = 0; ix <= nx; ix++) {
        const x = r.min[0] + ((r.max[0] - r.min[0]) * ix) / nx, z = r.min[1] + ((r.max[1] - r.min[1]) * iz) / nz;
        positions[p++] = x; positions[p++] = height(x, z) + lift; positions[p++] = z;
      }
    }
    for (let iz = 0; iz < nz; iz++) {
      for (let ix = 0; ix < nx; ix++) {
        const a = base + iz * (nx + 1) + ix, b = a + 1, c = a + nx + 1, d = c + 1;
        // same order as terrainMesh: clockwise from above is Babylon's front face
        indices[k++] = a; indices[k++] = b; indices[k++] = c;
        indices[k++] = b; indices[k++] = d; indices[k++] = c;
      }
    }
    base += (nx + 1) * (nz + 1);
  });
  return { positions, indices };
}

/** Avenues and streets as two merged meshes: they receive shadows, cast none, and are never picked or collided with. */
export function buildRoads(scene: Scene, rects: readonly RoadRect[], height: Height): Mesh[] {
  return (["avenue", "road"] as const).flatMap((kind) => {
    const mine = rects.filter((r) => r.kind === kind);
    if (mine.length === 0) return [];
    const { positions, indices } = roadVertexData(mine, height, STYLE[kind].lift);
    const normals: number[] = [];
    VertexData.ComputeNormals(positions, indices, normals);
    const vd = new VertexData();
    vd.positions = positions;
    vd.indices = indices;
    vd.normals = normals;
    const mesh = new Mesh(`roads:${kind}`, scene);
    vd.applyToMesh(mesh);
    const m = new StandardMaterial(`roads:${kind}`, scene);
    m.diffuseColor = Color3.FromHexString(STYLE[kind].color);
    m.specularColor = Color3.Black();
    mesh.material = m;
    mesh.receiveShadows = true;
    mesh.isPickable = false;
    mesh.checkCollisions = false;
    return [mesh];
  });
}
